import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFastOrderCsvValidation } from '@hooks-api/fortune/use-api-fast-order-csv-validation'
import { type FastOrderFileError } from '@hooks-api/types/dataModel/fast-order'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { AxiosError } from 'axios'
import { useHistory } from 'react-router-dom-v5'
import { useButler } from '../../../fp-b2b-shared/ai-purchase-order/components/ButlerProvider/useButler'
import { useStmMallCode } from '../../hooks/use-stm-mall-code'
import { useStmQuickOrderStore } from '../../hooks/use-stm-quick-order-store'
import { CSV_VALIDATION_ERROR_LEVEL, DIALOG_CSV_VALIDATION_RESULT, type DialogCsvValidationResultProps } from '../components/DialogCsvValidationResult/DialogCsvValidationResult'
import { toCsvButlerItems, toCsvFileErrorLine, toCsvValidationErrorLines } from '../utils/fast-order-transformer'

const isFileError = (error: unknown): error is AxiosError<FastOrderFileError> =>
  error instanceof AxiosError && error.response?.status === 422 && !!error.response.data?.file_error_code

export const useCsvValidationSubmit = () => {
  const history = useHistory()
  const mallCode = useStmMallCode()
  const { routes } = useButler()
  const setCsvResult = useStmQuickOrderStore(state => state.setCsvResult)
  const { openDialog } = useDialog<DialogCsvValidationResultProps>(DIALOG_CSV_VALIDATION_RESULT)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { trigger, isMutating } = useApiFastOrderCsvValidation({ mall_code: mallCode })

  const submit = useCallbackEvent(async (file: File) => {
    try {
      const { data: { data: result } } = await trigger({ file })
      if (!result.passed) {
        openDialog({ errorLevel: CSV_VALIDATION_ERROR_LEVEL.ROW, lines: toCsvValidationErrorLines(result.rows) })
        return
      }
      setCsvResult({ fileName: file.name, items: toCsvButlerItems(result.items ?? []) })
      history.push(routes.csvRecommendations)
    }
    catch (error) {
      if (isFileError(error) && error.response) {
        openDialog({ errorLevel: CSV_VALIDATION_ERROR_LEVEL.FILE, lines: [toCsvFileErrorLine(error.response.data)] })
        return
      }
      openSnackbar({ message: '驗證未完成，請再試一次', severity: 'error' })
    }
  })

  return { submit, isSubmitting: isMutating }
}
